import { createSelector } from '@reduxjs/toolkit';
import { last, merge } from 'lodash';
import objectScan from 'object-scan';
import { getProductTypes } from 'redux/selectors/types';

export const getExperience = (state) => state.experience;

// Will only have 1 item under the resources object
export const getComplexResource = (state) => Object.values(state.resources)[0];

export const getComplexResourceGroups = createSelector(
  [getComplexResource, getExperience, getProductTypes, (_, params) => params],
  (complexResource, experience, productTypes, params = {}) => {
    const { journeyType = 'departure', productType = 'coach' } = params;

    if (!complexResource) {
      return [];
    }

    const experienceItem = objectScan(['**'], {
      rtn: 'value',
      abort: true,
      filterFn: ({ value }) => value?.resource_id === complexResource.id,
    })(experience);
    const keys = {
      departure: 'from_route_stop_id',
      return: 'to_route_stop_id',
    };
    const productTypeNames = {
      ferry: 'Ferry',
      coach: 'Coach Transfers and Tours',
    };
    const productTypeId = productTypes.find(
      (item) => item.name === productTypeNames[productType]
    ).id;
    let complexResourceGroups = {};

    objectScan(['**'], {
      filterFn: ({ value, getKey, parents }) => {
        const resourceGroups = parents.filter(
          (item) => item?.type === 'ResourceGroup'
        );
        const parentGroup = resourceGroups[0];
        const keysArray = getKey();
        const isTarget =
          value?.key === keys[journeyType] &&
          parentGroup.pick === 0 &&
          parentGroup.product_type_id === productTypeId &&
          parentGroup.auto_pick_default_selections === false &&
          keysArray.at(-1) === 0; // It must be the first item in the package_options group

        if (!isTarget) {
          return false;
        }

        const packages = parents.filter((item) => item?.type === 'Package');
        const bookableOption = {
          key: getKey(true),
          type: 'booking-option',
          resource_id: complexResource.id,
          hasFerry: experienceItem.hasFerry || false,
          hasAccommodation: experienceItem.hasAccommodation || false,
          groupId: parentGroup.id,
          packageId: parentGroup.package_items[0].id,
          packageItems: parentGroup.package_items,
          package_options: parentGroup.package_options[0],
          packageOptionSets: parentGroup.package_options,
        };
        const bookablePackage = packages[0];
        const packageItems = parents.filter(
          (item) => item?.type === 'PackageItem'
        );
        const parentPackageItem = packageItems[0];
        let temp = {};

        // For deep nested packages, e.g. package#336
        if (packages.length === 2 && resourceGroups.length === 3) {
          const rootPackage = last(packages);
          const group = resourceGroups[1];
          const hasFerry =
            group.pick === 0 && group.auto_pick_default_selections === false;
          const options = {
            ...bookableOption,
            title: bookablePackage.name.trim(),
            product_type_id: bookablePackage.product_type_id,
            resourceTitle: rootPackage.name.trim(),
          };

          if (hasFerry) {
            options.resourceGroupId = group.id;
            options.packageItemId = parentPackageItem.id;
          }

          temp = {
            [rootPackage.id]: {
              // K'gari Ultimate 5 Day/4 Night Tour (ex Hervey Bay)
              title: rootPackage.name.trim(),
              options: {
                [group.id]: {
                  // Travel to Fraser Island
                  title: group.title,
                  options: {
                    [bookablePackage.id]: options,
                  },
                },
              },
            },
          };
        }

        // For shallow nested packages, e.g. package#8
        if (packages.length === 1 && resourceGroups.length === 2) {
          const rootGroup = last(resourceGroups);
          const hasFerry =
            rootGroup.pick === 0 &&
            rootGroup.auto_pick_default_selections === false;
          const options = {
            ...bookableOption,
            title: bookablePackage.name.trim(),
            product_type_id: bookablePackage.product_type_id,
            resourceTitle: rootGroup.title.trim(),
          };

          if (hasFerry) {
            options.resourceGroupId = rootGroup.id;
            options.packageItemId = parentPackageItem.id;
          }

          temp = {
            [rootGroup.id]: {
              // Travel to K'gari (Fraser Island)
              title: rootGroup.title.trim(),
              options: {
                [bookablePackage.id]: options,
              },
            },
          };
        }

        // For shallower nested packages, e.g. package#7
        if (packages.length === 0 && resourceGroups.length === 1) {
          temp = {
            [bookableOption.groupId]: {
              // K'gari Explorer Day Tour (ex Rainbow Beach)
              title: complexResource.name.trim(),
              options: {
                [bookableOption.packageId]: {
                  ...bookableOption,
                  product_type_id: complexResource.product_type_id,
                  resourceTitle: complexResource.name.trim(),
                },
              },
            },
          };
        }

        complexResourceGroups = merge(complexResourceGroups, temp);
      },
    })(complexResource.resource_groups);

    const complexResourceList = Object.values(complexResourceGroups);

    // Convert object options to array options
    objectScan(['**'], {
      filterFn: ({ value }) => {
        if (value?.options) {
          value.options = Object.values(value.options);
        }
      },
    })(complexResourceList);

    return complexResourceList;
  }
);

export const getQuickCatOptions = createSelector(
  [getComplexResource],
  (complexResource) => {
    const availableOptions = objectScan(['**'], {
      rtn: 'value',
      filterFn: ({ value }) =>
        value?.type === 'ResourceGroup' && value?.pick === 0,
    })(complexResource);

    return availableOptions.map((option) => ({
      id: option.id,
      title: option.title,
      packageItems: option.package_items.map((item) => ({
        id: item.id,
        title: item.resource.name,
      })),
    }));
  }
);
